import { z } from 'zod/v4';

import type {
  DeploymentEnvironment,
  DeploymentMode,
  DeploymentPhase,
  DeploymentPipeline,
  DeploymentPipelineTarget,
  DeploymentStatsDays,
  DeploymentStatus,
} from './deployments';
import {
  getMetadataLabeledOptionLabel,
  getMetadataLabeledOptions,
  getMetadataLabeledOptionValues,
} from './type-metadata';
import type {
  DevexCodexFeedbackRejectionReason,
  DevexCodexFeedbackReplySentiment,
  DevexReviewLatencyGroupBy,
  DevexReviewLatencySizeBucket,
  DevexReviewLatencyTeam,
} from '@shared/dev-console/devex-metrics';
import type { Lane } from '@shared/jobs/constants';
import type { PatientId } from '@shared/types';

export type {
  DeploymentEnvironment,
  DeploymentMode,
  DeploymentPhase,
  DeploymentPipeline,
  DeploymentPipelineTarget,
  DeploymentStatsDays,
  DeploymentStatus,
  DevexCodexFeedbackRejectionReason,
  DevexCodexFeedbackReplySentiment,
};
export type { DevexReviewLatencyGroupBy, DevexReviewLatencySizeBucket, DevexReviewLatencyTeam };

export interface QueueOverview {
  canonicalJob: string;
  pod: string;
  jobNames: string[];
  mainQueueUrl: string;
  dlqUrl: string;
  mainQueueCount: number;
  dlqCount: number;
}

export interface DlqMessageSummary {
  messageId: string;
  jobName: string;
  traceId: string;
  sentTimestamp: number;
  receiveCount: number;
  body: string;
  messageAttributes: Record<string, string>;
}

export interface JobLogEntry {
  timestamp: string;
  level: string;
  message: string;
  context: Record<string, unknown>;
}

export interface JobSearchFilters {
  canonicalJob?: string;
  environment?: 'production' | 'staging';
  jobName?: string;
  traceId?: string;
  level?: string;
  startTime?: string;
  endTime?: string;
}

export type WorkflowLifecyclePhase =
  | 'run.created'
  | 'step.started'
  | 'step.completed'
  | 'step.failed'
  | 'run.completed';

export type WorkflowRunStatus = 'running' | 'succeeded' | 'failed' | 'halted' | 'retrying' | 'unknown';

export interface WorkflowLifecycleEvent {
  timestamp: string;
  phase: WorkflowLifecyclePhase;
  workflowId: string;
  runId: string;
  stepKey: string | null;
  completionType: string | null;
  errorMessage: string | null;
  failureType: string | null;
  errorStack: string | null;
  payload: Record<string, unknown>;
}

export interface WorkflowLogEntry {
  timestamp: string;
  level: string;
  message: string;
  workflowId: string | null;
  runId: string;
  stepKey: string | null;
  payload: Record<string, unknown>;
}

export interface WorkflowRunSummary {
  runId: string;
  workflowId: string;
  status: WorkflowRunStatus;
  currentStepKey: string | null;
  startedAt: string | null;
  lastEventAt: string;
  completedAt: string | null;
  durationMs: number | null;
  eventCount: number;
  failureMessage: string | null;
  failureType: string | null;
  logContext: Record<string, unknown>;
}

export interface WorkflowDefinitionSummary {
  workflowId: string;
  runCount: number;
  failedRunCount: number;
  /** Null when the workflow is registered but has no runs in the selected window. */
  lastEventAt: string | null;
  observedStepKeys: string[];
}

export interface WorkflowRunsResponse {
  runs: WorkflowRunSummary[];
  definitions: WorkflowDefinitionSummary[];
  eventsScanned: number;
  /**
   * Runs listed from the index alone because the request hit its read limit. Their rows carry no
   * events, so search cannot match their metadata.
   */
  unreadRunCount: number;
}

export type WorkflowStepStatus = 'running' | 'succeeded' | 'failed' | 'halted' | 'unknown';

export type WorkflowStepTimelineEntry =
  | {
      type: 'lifecycle';
      timestamp: string;
      event: WorkflowLifecycleEvent;
    }
  | {
      type: 'log';
      timestamp: string;
      log: WorkflowLogEntry;
    };

export interface WorkflowStepDetail {
  stepKey: string;
  status: WorkflowStepStatus;
  startedAt: string | null;
  completedAt: string | null;
  durationMs: number | null;
  failureMessage: string | null;
  failureType: string | null;
  errorStack: string | null;
  timeline: WorkflowStepTimelineEntry[];
}

export type WorkflowTimelineEntry =
  | {
      type: 'run';
      timestamp: string;
      event: WorkflowLifecycleEvent;
    }
  | {
      type: 'step';
      timestamp: string;
      step: WorkflowStepDetail;
    };

export interface WorkflowRunDetailResponse {
  run: WorkflowRunSummary;
  events: WorkflowLifecycleEvent[];
  input: unknown;
  steps: WorkflowStepDetail[];
  timeline: WorkflowTimelineEntry[];
}

export type RedriveMode = 'naive' | 'smart';

export interface RedriveRequest {
  dlqUrl: string;
  messageIds: string[];
  redriveMode?: RedriveMode;
}

export interface RedriveJobDetail {
  jobName: string;
  traceId: string;
  newMessageId: string;
  /** Which DLQ messages this job covers. Naive: always 1. Smart: 1+ when deduped. */
  sourceMessageIds: string[];
}

export interface RedriveResult {
  redriveMode: RedriveMode;
  /** Jobs successfully sent to main queue */
  jobsSent: number;
  /** Jobs that failed to send */
  jobSendsFailed: number;
  /** DLQ messages deleted after successful sends */
  dlqMessagesDeleted: number;
  /** DLQ messages that couldn't be deleted (job was still sent — non-fatal) */
  dlqDeletesFailed: number;
  /** Requested message IDs not found in DLQ */
  messagesNotFound: number;
  /** Per-job detail for results table */
  jobDetails: RedriveJobDetail[];
  /** Human-readable errors in occurrence order */
  errors: string[];
}

export interface SmartRedriveInfo {
  rootJobName: string;
  description: string;
}

/** Sent as the `env` query param on every postgres-jobs and job-schedules route, POSTs included; defaults to production. */
export interface JobsDbEnvironmentQuery {
  env?: DeploymentEnvironment;
}

export const POSTGRES_JOB_STATUSES = ['SCHEDULED', 'PENDING', 'RUNNING', 'SUCCEEDED', 'FAILED', 'DISCARDED'] as const;
export type PostgresJobStatus = (typeof POSTGRES_JOB_STATUSES)[number];

export interface PostgresJob {
  id: string;
  name: string;
  lane: Lane;
  pod: string;
  status: PostgresJobStatus;
  payload: unknown;
  attemptCount: number;
  maxAttempts: number;
  lastError: string | null;
  lastAttemptedAt: string | null;
  runAt: string;
  scheduleId: string | null;
  dispatchLeaseExpiresAt: string | null;
  traceId: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface JobSchedule {
  id: string;
  jobName: string;
  lane: Lane;
  pod: string;
  maxAttempts: number;
  cronExpression: string;
  enabled: boolean;
  nextRunAt: string;
  lastRunAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface JobScheduleListResponse {
  schedules: JobSchedule[];
}

export interface JobScheduleSetEnabledRequest {
  scheduleId: string;
  enabled: boolean;
}

export interface JobScheduleSetEnabledResponse {
  schedule: JobSchedule;
}

export interface PostgresJobListResponse {
  jobs: PostgresJob[];
  nextCursor: string | null;
}

export interface PostgresJobNamesResponse {
  names: string[];
}

export interface PostgresJobDiscardResult {
  job: PostgresJob;
}

export interface PostgresJobsDiscardResult {
  succeeded: PostgresJobDiscardResult[];
  failed: Array<{
    jobId: string;
    message: string;
  }>;
}

export interface PostgresJobRedriveResult {
  job: PostgresJob;
  messageId: string | null;
}

export interface PostgresJobsRedriveResult {
  succeeded: PostgresJobRedriveResult[];
  failed: Array<{
    jobId: string;
    message: string;
  }>;
}

const mobileAppDeploymentAppOptionSchema = z.object({
  patient: z.literal('patient').meta({ label: 'Patient' }),
  provider: z.literal('provider').meta({ label: 'Provider' }),
});

const mobileAppDeploymentPlatformOptionSchema = z.object({
  all: z.literal('all').meta({ label: 'All' }),
  ios: z.literal('ios').meta({ label: 'iOS' }),
  android: z.literal('android').meta({ label: 'Android' }),
});

const mobileAppDeploymentTypeFilterOptionSchema = z.object({
  all: z.literal('all').meta({ label: 'All' }),
  ota: z.literal('ota').meta({ label: 'OTA' }),
  native: z.literal('native').meta({ label: 'Native' }),
});

export const MOBILE_APP_DEPLOYMENT_APPS = getMetadataLabeledOptionValues(mobileAppDeploymentAppOptionSchema);
export const MOBILE_APP_DEPLOYMENT_PLATFORMS = getMetadataLabeledOptionValues(mobileAppDeploymentPlatformOptionSchema);
export const MOBILE_APP_DEPLOYMENT_TYPE_FILTERS = getMetadataLabeledOptionValues(
  mobileAppDeploymentTypeFilterOptionSchema
);

export const mobileAppDeploymentAppSchema = z.enum(MOBILE_APP_DEPLOYMENT_APPS);
export const mobileAppDeploymentPlatformSchema = z.enum(MOBILE_APP_DEPLOYMENT_PLATFORMS);
export const mobileAppDeploymentTypeFilterSchema = z.enum(MOBILE_APP_DEPLOYMENT_TYPE_FILTERS);
export const mobileAppDeploymentAppOptions = getMetadataLabeledOptions(mobileAppDeploymentAppOptionSchema);
export const mobileAppDeploymentPlatformOptions = getMetadataLabeledOptions(mobileAppDeploymentPlatformOptionSchema);
export const mobileAppDeploymentTypeFilterOptions = getMetadataLabeledOptions(
  mobileAppDeploymentTypeFilterOptionSchema
);
export const getMobileAppDeploymentPlatformLabel = (value: MobileAppDeploymentPlatform) =>
  getMetadataLabeledOptionLabel(mobileAppDeploymentPlatformOptions, value);
export const getMobileAppDeploymentTypeFilterLabel = (value: MobileAppDeploymentTypeFilter) =>
  getMetadataLabeledOptionLabel(mobileAppDeploymentTypeFilterOptions, value);

export type MobileAppDeploymentApp = z.infer<typeof mobileAppDeploymentAppSchema>;
export type MobileAppDeploymentPlatform = z.infer<typeof mobileAppDeploymentPlatformSchema>;
export type MobileAppDeploymentTypeFilter = z.infer<typeof mobileAppDeploymentTypeFilterSchema>;

export const mobileAppDeploymentListItemSchema = z.object({
  id: z.number(),
  versionId: z.number(),
  deploymentRunKey: z.string(),
  platform: mobileAppDeploymentPlatformSchema,
  deploymentType: z.string(),
  appVersion: z.string(),
  runtimeVersion: z.string(),
  commitHash: z.string(),
  triggeredBy: z.string(),
  deploymentRunUrl: z.string(),
  groupUpdateId: z.string().nullable(),
  iosUpdateId: z.string().nullable(),
  androidUpdateId: z.string().nullable(),
  iosEasBuildId: z.string().nullable(),
  androidEasBuildId: z.string().nullable(),
  createdAt: z.string(),
});

export const mobileAppDeploymentListResponseSchema = z.object({
  deployments: z.array(mobileAppDeploymentListItemSchema),
  pagination: z.object({
    hasNextPage: z.boolean(),
    limit: z.number().optional(),
    page: z.number().optional(),
  }),
  sourceEnvironment: z.string(),
});

export type MobileAppDeploymentListItem = z.infer<typeof mobileAppDeploymentListItemSchema>;
export type MobileAppDeploymentListResponse = z.infer<typeof mobileAppDeploymentListResponseSchema>;

export interface PostgresJobFilters {
  name?: string;
  pod?: string;
  status?: PostgresJobStatus;
  lane?: Lane;
  createdAfter?: string;
  createdBefore?: string;
  cursor?: string;
  limit?: number;
}

export interface EntityLookup {
  type: 'patient' | 'signUpLead' | 'appointment' | 'user';
  id: number;
}

export interface PaginationMetadata {
  page: number;
  pageSize: number;
  query: string;
  totalCount: number;
  totalPages: number;
}

export interface PaginatedResponse<T> {
  items: T[];
  pagination: PaginationMetadata;
}

export interface PatientListItem {
  patientId: number;
  userId: number;
  displayName: string;
  isActive: boolean | null;
  createdAt: string;
  archivedAt: string | null;
  phaseOfCare: string | null;
  healthieId: string | null;
}

export interface PatientDetail {
  patientId: number;
  userId: number;
  displayName: string;
  healthieId: string | null;
  isActive: boolean | null;
  timeZone: string | null;
  createdAt: string;
  archivedAt: string | null;
  phaseOfCare: string | null;
  referralSource: string | null;
  referralProvider: string | null;
}

export const PATIENT_ACTIVITY_EXPORT_FORMATS = ['csv', 'json'] as const;
export type PatientActivityExportFormat = (typeof PATIENT_ACTIVITY_EXPORT_FORMATS)[number];

export interface PatientActivityExportSummary {
  patientId: PatientId;
  estimatedAt: string;
  totalActivities: number;
  mealCount: number;
  giSymptomsCount: number;
  movementCount: number;
  hydrationCount: number;
  mealIngredientMacroCount: number;
  mealInsightsCount: number;
  reactionCount: number;
}

export interface PatientCareTeamProvider {
  providerId: number;
  type: string;
  state: string | null;
  firstName: string;
  lastName: string | null;
  email: string;
  photoUrl: string | null;
  isActive: boolean | null;
}

export interface PatientCareTeamAssignment {
  id: number;
  providerType: string;
  assignedAt: string;
  provider: PatientCareTeamProvider;
}

export interface PatientAppointmentProvider {
  providerId: number;
  type: string;
  firstName: string;
  lastName: string | null;
}

export interface PatientAppointment {
  id: number;
  date: string;
  start: string;
  end: string;
  length: number | null;
  title: string | null;
  status: string | null;
  appointmentType: string | null;
  contactType: string | null;
  reason: string | null;
  notes: string | null;
  healthieId: string | number | null;
  createdAt: string;
  provider: PatientAppointmentProvider;
}

export interface PatientSignUpAttribution {
  utmCampaign: string | null;
  utmContent: string | null;
  utmMedium: string | null;
  utmSource: string | null;
  utmTerm: string | null;
  fbclid: string | null;
  gclid: string | null;
  ttclid: string | null;
  oppref: string | null;
  irclickid: string | null;
  imRef: string | null;
}

export interface PatientSignUpHealthProfile {
  wantHelpWith: string[];
  levelOfCommitment: string | null;
  activityLevel: string | null;
  eatingHabits: string | null;
}

export interface PatientSignUpProviderReferral {
  publicLink: string;
  referralType: string | null;
  referral: {
    providerName: string | null;
    providerNpi: string | null;
    providerType: string | null;
    referralSourceType: string | null;
    referralType: string | null;
  } | null;
}

export interface PatientSignUpPatientReferral {
  publicId: string;
  referringPatientId: number;
  referringPatientName: string;
}

export interface PatientSignUpLead {
  id: number;
  createdAt: string;
  completedAt: string | null;
  abandonRecordedAt: string | null;
  currentStepId: string | null;
  variation: string | null;
  insuranceCompany: string | null;
  referralSourceType: string | null;
  referralSourceSubtype: string | null;
  referralSource: string | null;
  referralName: string | null;
  referringProviderType: string | null;
  referringProviderState: string | null;
  referringProviderCity: string | null;
  referringProviderNpi: string | null;
  wasPreauthedSuccessfully: boolean | null;
  attribution: PatientSignUpAttribution;
  healthProfile: PatientSignUpHealthProfile;
  verificationMethod: string | null;
  verificationStatus: string | null;
  landingPageVariation: string | null;
  providerReferral: PatientSignUpProviderReferral | null;
  patientReferral: PatientSignUpPatientReferral | null;
}

export interface PatientInsuranceEligibility {
  id: number;
  createdAt: string;
  status: string | null;
  workflowStatus: string | null;
  networkStatus: string | null;
  estimatedCost: number | null;
  payerName: string | null;
  planName: string | null;
  planStatus: string | null;
  coverageStartDate: string | null;
  source: string | null;
}

export interface PatientWaitlistEntry {
  id: number;
  createdAt: string;
  status: string | null;
  priority: number | string | null;
  appointmentType: string | null;
  lastCheckedAt: string | null;
}

export interface PatientSignUpData {
  patient: {
    patientCreationState: string | null;
    firstApptCreatedAt: string | null;
    hasSeenOnboarding: boolean;
    patientCreatedAt: string;
  };
  signUpLead: PatientSignUpLead | null;
  insuranceEligibility: PatientInsuranceEligibility | null;
  waitlist: PatientWaitlistEntry[];
}

export interface PatientChatAttachment {
  id: number;
  type: string;
  fileName: string | null;
}

export interface PatientChatMessage {
  id: number;
  createdAt: string;
  role: string;
  content: string;
  isHelpful: boolean | null;
  classification: string | null;
  quickReplies: string[];
  attachments: PatientChatAttachment[];
}

export interface PatientChatSession {
  id: number;
  createdAt: string;
  title: string | null;
  summary: string | null;
  isPrivate: boolean;
  lastMessageSentAt: string | null;
  providerIsHelpful: boolean | null;
  messageCount: number;
  messages: PatientChatMessage[];
}

export interface ProviderListItem {
  email: string;
  firstName: string;
  isActive: boolean | null;
  lastName: string;
  photoUrl: string | null;
  providerId: number;
  startDate: string;
  state: string | null;
  tag: number | null;
  type: string;
  userId: number;
}

export interface ProviderDetail {
  about: string | null;
  approachToCare: string | null;
  city: string | null;
  education: string | null;
  email: string;
  firstName: string;
  healthieId: string | null;
  isActive: boolean | null;
  lastName: string;
  npi: string | null;
  phone: string | null;
  photoUrl: string | null;
  providerId: number;
  startDate: string;
  state: string | null;
  tag: number | null;
  type: string;
  userId: number;
}

export interface ProviderCalendarWorkingHour {
  day: string;
  start: string;
  end: string;
}

export interface ProviderCalendarSettings {
  caseloadGoal: number | null;
  activeCaseload: number;
  maxAppointmentsPerDay: number | null;
  isAcceptingNewInitials: boolean;
  allowReferralInitials: boolean;
  minInitialBookingWindowDays: number | null;
  maxInitialsPerDay: number | null;
  workingHours: ProviderCalendarWorkingHour[];
}

export interface ProviderLicense {
  id: number;
  state: string | null;
  licenseNumber: string | null;
  type: string | null;
  issueDate: string;
  expirationDate: string;
}

export interface ProviderCredential {
  id: number;
  payerName: string | null;
  candidPayerName: string | null;
  contractLabel: string | null;
  state: string | null;
  status: string | null;
  effectiveDate: string | null;
  applicationSubmissionDate: string | null;
  credentiallingCompleteDate: string | null;
}

export interface ProviderExternalSubcalendar {
  id: number;
  calendarId: string;
  name: string;
  isEnabled: boolean;
}

export interface ProviderNourishExternalCalendar {
  status: string;
  email: string | null;
  pullEventsEnabled: boolean;
  pushEventsEnabled: boolean;
  lastSyncedAt: string | null;
  connectedAt: string;
  subcalendars: ProviderExternalSubcalendar[];
}

export interface ProviderHealthieCalendarEntry {
  name: string;
  calendarId: string;
}

export interface ProviderHealthieExternalCalendar {
  email: string | null;
  pullFromCalendar: boolean;
  addToCalendar: boolean;
  pulledInCalendars: string[];
  calendarList: ProviderHealthieCalendarEntry[];
  hasRefreshToken: boolean;
  hasRanFirstSync: boolean;
}

export interface ProviderExternalCalendarData {
  nourish: ProviderNourishExternalCalendar | null;
  healthie: ProviderHealthieExternalCalendar | null;
}

export interface ProviderCalendarSourceEvent {
  id: number | string;
  start: string;
  end: string;
  title: string | null;
  type?: string | null;
  status?: string | null;
  patientName?: string | null;
  source: 'nourish' | 'healthie';
  inBoth: boolean;
}

export interface ProviderExternalCalendarEvent {
  id: number;
  start: string;
  end: string;
  title: string | null;
  blocksAvailability: boolean;
  inBoth: boolean;
}

export interface ProviderCalendarEventsData {
  appointments: ProviderCalendarSourceEvent[];
  blocks: ProviderCalendarSourceEvent[];
  healthieAppointments: ProviderCalendarSourceEvent[];
  healthieBlocks: ProviderCalendarSourceEvent[];
  healthieExternalEvents: ProviderCalendarSourceEvent[];
  externalEvents: ProviderExternalCalendarEvent[];
  workingHours: ProviderCalendarWorkingHour[];
}

export interface DlqMessagesResponse {
  dlqUrl: string;
  messages: DlqMessageSummary[];
  smartRedriveInfo: SmartRedriveInfo | null;
}

export interface JobDetailResponse {
  traceId: string;
  logs: JobLogEntry[];
}

export interface DiscardResult {
  succeeded: number;
  failed: number;
  notFound: number;
}

export interface RunbookMapping {
  id: number;
  canonicalJob: string;
  notionUrl: string;
  notionPageId: string | null;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
}

export interface RunbookSearchResult {
  title: string;
  url: string;
  pageId: string;
}

export interface RunbookSummarizeRequest {
  canonicalJob: string;
  notionPageId: string;
  dlqMessage?: DlqMessageSummary;
  logEntries?: JobLogEntry[];
}

export type DevexReviewLatencyScope = 'org' | 'team';

export interface DevexReviewLatencyPoint {
  date: string;
  avg: number | null;
  p50: number | null;
  p75: number | null;
  p90: number | null;
  p99: number | null;
  sampleSizePrs: number;
  teamSlug?: string;
  groupKey?: string;
}

export interface DevexReviewLatencyResponse {
  series: DevexReviewLatencyPoint[];
}

export type DevexCodexFeedbackPullRequestState = 'OPEN' | 'MERGED' | 'CLOSED';
export type DevexCodexFeedbackReactionFilter = 'all' | 'negative';

export interface DevexCodexFeedbackReactions {
  thumbsDown: number;
  thumbsUp: number;
}

export interface DevexCodexFeedbackSentimentSummary {
  helpfulCount: number;
  notHelpfulCount: number;
  totalComments: number;
  unratedCount: number;
}

export interface DevexCodexFeedbackSentimentPoint extends DevexCodexFeedbackSentimentSummary {
  date: string;
  helpfulPct: number;
  notHelpfulPct: number;
  unratedPct: number;
}

export interface DevexCodexFeedbackSentiment {
  points: DevexCodexFeedbackSentimentPoint[];
  summary: DevexCodexFeedbackSentimentSummary;
}

export interface DevexCodexFeedbackComment {
  authorLogin: string;
  body: string;
  createdAt: string;
  reactions: DevexCodexFeedbackReactions;
  updatedAt: string;
  url: string;
}

export interface DevexCodexFeedbackItem {
  pullRequest: {
    authorLogin: string;
    number: number;
    state: DevexCodexFeedbackPullRequestState;
    title: string;
    url: string;
  };
  thread: {
    id: string;
    path: string;
    line: number | null;
    startLine: number | null;
    isOutdated: boolean;
    isResolved: boolean;
    commentsTruncated: boolean;
  };
  codexComment: DevexCodexFeedbackComment;
  rejectionReason: DevexCodexFeedbackRejectionReason | null;
  reply: DevexCodexFeedbackComment;
  replySentiment: DevexCodexFeedbackReplySentiment;
}

export interface DevexCodexFeedbackResponse {
  generatedAt: string;
  filters: {
    botLogins: string[];
    days: number;
    includeBotReplies: boolean;
    prNumbers: number[];
    reactionFilter: DevexCodexFeedbackReactionFilter;
    states: DevexCodexFeedbackPullRequestState[];
  };
  scannedPullRequestCount: number;
  replyCount: number;
  pullRequestCount: number;
  sentiment: DevexCodexFeedbackSentiment;
  items: DevexCodexFeedbackItem[];
}

export interface DeploymentPullRequest {
  number: number;
  title: string;
  url: string;
  authorLogin: string;
  /** The PR's own merged commit; null for deployments recorded before it was captured. */
  commitSha: string | null;
}

export interface DeploymentRecord {
  environment: DeploymentEnvironment;
  mode: DeploymentMode;
  status: DeploymentStatus;
  /** Present while status is in_progress; null for terminal rows. */
  phase: DeploymentPhase | null;
  commitSha: string;
  commitShaShort: string;
  commitMessage: string | null;
  triggeredBy: string;
  workflowFile: string;
  workflowRunId: string;
  workflowRunUrl: string;
  startedAt: string;
  completedAt: string | null;
  durationSeconds: number | null;
  buildDurationSeconds: number | null;
  deployDurationSeconds: number | null;
  commitCount: number | null;
  targetCommitSha: string | null;
  /** Present when status is skipped because a newer SHA superseded this run. */
  supersededBySha: string | null;
  jobServiceName: string | null;
  actorEmail: string | null;
  pullRequests: DeploymentPullRequest[];
  /** Present when ingest/GitHub recorded why a failure or cancel happened. */
  failureReason: string | null;
  /** GitHub Actions job that failed, when known. */
  failedJobName: string | null;
  /** Short log/annotation excerpt for triage; never a full job log dump. */
  failureLogExcerpt: string | null;
  updatedAt: string;
}

export interface DeploymentCurrentResponse {
  items: Array<{
    environment: DeploymentEnvironment;
    current: DeploymentRecord | null;
  }>;
}

export interface DeploymentHistoryResponse {
  items: DeploymentRecord[];
  nextCursor: string | null;
}

export interface DeploymentStatsBucket {
  day: string;
  total: number;
  success: number;
  failure: number;
  /** Sum of commitCount across deploys that day (missing counts treated as 0). */
  commitCount: number;
  /** Mean durationSeconds for deploys that day with a recorded duration; null if none. */
  avgDurationSeconds: number | null;
}

export interface DeploymentStatsResponse {
  days: DeploymentStatsDays;
  environment: DeploymentEnvironment | 'all';
  mode: DeploymentMode | 'all';
  total: number;
  success: number;
  failure: number;
  cancelled: number;
  skipped: number;
  successRate: number | null;
  averageDurationSeconds: number | null;
  p50DurationSeconds: number | null;
  p90DurationSeconds: number | null;
  buckets: DeploymentStatsBucket[];
}

export interface DeploymentRollbackRequest {
  environment: DeploymentEnvironment;
  commitSha: string;
  /** Runtime SHA displayed when the operator confirmed; the workflow fails closed if the runtime has since moved. */
  deployedCommitSha?: string;
  jobServiceName?: string;
  rollbackDatabase?: boolean;
}

export type PrismaRollbackMigrationClassification = 'reversible' | 'irreversible' | 'missing';

export interface PrismaRollbackMigration {
  name: string;
  migrationSqlPath: string;
  downSqlPath: string | null;
  downSql: string | null;
  classification: PrismaRollbackMigrationClassification;
}

export interface DeploymentRollbackPreflightRequest {
  environment: DeploymentEnvironment;
  commitSha: string;
  /** Runtime SHA displayed to the operator; when set it is the preview baseline instead of workflow history. */
  deployedCommitSha?: string;
}

export interface DeploymentRollbackPreflightResponse {
  deployedSha: string;
  targetSha: string;
  databaseRollbackStatus: 'not-required' | 'available' | 'unavailable';
  migrations: PrismaRollbackMigration[];
  counts: Record<PrismaRollbackMigrationClassification, number>;
}

export interface DeploymentRollbackResponse {
  workflowFile: string;
  workflowUrl: string;
  environment: DeploymentEnvironment;
  commitSha: string;
  jobServiceName: string | null;
  dispatchedAt: string;
}

export interface DeploymentCancelRequest {
  workflowRunId: string;
}

export interface MergeFreezeRequest {
  action: 'pause' | 'resume';
  reason?: string;
}

export interface MergeFreezeStatusResponse {
  enabled: boolean;
}

export interface MergeFreezeResponse {
  action: MergeFreezeRequest['action'];
  workflowFile: string;
  workflowUrl: string;
  dispatchedAt: string;
}

export interface DeploymentCancelResponse {
  workflowRunId: string;
  workflowRunUrl: string;
  cancelledAt: string;
}

export interface DeploymentPipelineWorkflowStatus {
  workflowFile: string;
  state: string;
  htmlUrl: string;
  statusWarning?: string;
}

export interface DeploymentPipelineStatus {
  pipeline: DeploymentPipeline;
  /** True when every tracked workflow for the pipeline is not `active`. */
  disabled: boolean;
  /** False when GitHub does not allow reading one or more workflow states. */
  statusAvailable: boolean;
  warning?: string;
  workflows: DeploymentPipelineWorkflowStatus[];
}

export interface DeploymentPipelineStatusResponse {
  items: DeploymentPipelineStatus[];
}

export interface DeploymentPipelineRequest {
  pipeline: DeploymentPipelineTarget;
  /** `disable` = stop deploys (disable workflows); `enable` = resume deploys. */
  action: 'disable' | 'enable';
}

export interface DeploymentPipelineResponse {
  pipeline: DeploymentPipelineTarget;
  action: 'disable' | 'enable';
  disabled: boolean;
  workflows: DeploymentPipelineWorkflowStatus[];
  toggledAt: string;
  /** Best-effort #eng-deployments notify; toggle still succeeds when not `sent`. */
  slackNotification?: 'sent' | 'skipped' | 'failed';
  slackWarning?: string;
}

export type DownstreamServiceIndicator = 'none' | 'minor' | 'major' | 'critical' | 'unknown';

export type DownstreamServiceTier = 'tier1' | 'tier2';

export interface DownstreamServiceStatus {
  name: string;
  indicator: DownstreamServiceIndicator;
  description: string;
  statusPageUrl: string;
  tier: DownstreamServiceTier;
  error?: string;
}

export interface DownstreamHealthResponse {
  checkedAt: string;
  services: DownstreamServiceStatus[];
}
